import React from 'react';
import { FaCircleChevronRight } from 'react-icons/fa6';

import EditableCell, {
  EditableSelectOption,
} from 'components/common/EditableCell';
import NumericInputField from 'components/common/NumericInputField';
import {
  TASKS_GRID_COLLAPSE_STEPS_ARIA_LABEL,
  TASKS_GRID_EXPAND_STEPS_ARIA_LABEL,
  TASKS_GRID_SELECT_UNIT_PLACEHOLDER,
  TASKS_GRID_TASK_ACTIONS_ARIA_LABEL,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { getInputTaskTypeLabel, IInputTaskBase } from 'interfaces/IInputTask';
import type { IInputTask } from 'interfaces/IInputTask';
import { formatCurrency } from 'utils/CurrencyUtils';
import MergeClassNames from 'utils/MergeClassNames';

import { isOtherCostTask } from './otherCosts';
import { calculateTaskCost, getTaskSteps } from './stepCalculations';
import {
  ALL_STEP_CATEGORY_KEY,
  filterStepsByCategory,
} from './stepCategoryFilters';
import { ApplicationTaskFields, formatDate } from './taskFactory';
import { RowKebabButton } from './TaskRowContextMenu';
import { TASK_DATE_CELL_ATTR } from './taskEntryFocus';
import { TASK_ROW_WIDTHS } from './taskRowLayout';
import TimingCell from './TimingCell';
import type { ContextMenuTarget } from './useStepContextMenu';

interface TaskSummaryRowProps {
  task: IInputTask;
  index: number;
  isExpanded: boolean;
  isMenuOpen: boolean;
  gUnits: GUnit[];
  selectedInputStartDate: string | null;
  activeStepCategoryKey: string;
  areaUnitOptions: EditableSelectOption[];
  /**
   * A filter is on and this task has nothing it matches (OP-2534): a slim row
   * with no expand chevron or caption, so matching tasks stand out.
   */
  isCompact?: boolean;
  /** The row's own "+ Add" for the filtered category, while collapsed. */
  rowAddInput?: React.ReactNode;
  /** Water rate / Target / Notes controls beside the name, while expanded. */
  inlineDetails?: React.ReactNode;
  commitTaskField: (
    task: IInputTask,
    patch: Partial<IInputTaskBase & ApplicationTaskFields>
  ) => void;
  onToggleExpand: (taskId: string) => void;
  /** Fires when the date picker closes, committed or not. */
  onDateEditEnd?: (taskId: string) => void;
  onOpenMenuFromKebab: (
    event: React.MouseEvent<HTMLButtonElement>,
    target: ContextMenuTarget
  ) => void;
}

// The task row itself: expand/collapse chevron, task type label and (while
// collapsed) a step-names caption, Date/Timing/Area/Area unit inline edits,
// and the task's own $/ha total with its three-dot actions button.
const TaskSummaryRow: React.FC<TaskSummaryRowProps> = ({
  task,
  index,
  isExpanded,
  isMenuOpen,
  gUnits,
  selectedInputStartDate,
  activeStepCategoryKey,
  areaUnitOptions,
  isCompact = false,
  rowAddInput,
  inlineDetails,
  commitTaskField,
  onToggleExpand,
  onDateEditEnd,
  onOpenMenuFromKebab,
}) => {
  const stepNamesLabel = filterStepsByCategory(
    getTaskSteps(task),
    activeStepCategoryKey
  )
    .map((step) => step.item)
    .filter(Boolean)
    .join(', ');
  const steps = getTaskSteps(task);
  const isOtherCost = isOtherCostTask(task);
  const isFiltered = activeStepCategoryKey !== ALL_STEP_CATEGORY_KEY;
  const total = calculateTaskCost(steps);
  const subtotal = calculateTaskCost(
    filterStepsByCategory(steps, activeStepCategoryKey)
  );
  return (
    <div
      className={MergeClassNames(
        'task-row-pill flex w-full rounded-md',
        isCompact ? 'my-0.5' : 'my-3'
      )}
      style={
        {
          '--col-date': TASK_ROW_WIDTHS.date,
          '--col-timing': TASK_ROW_WIDTHS.timing,
          '--col-area': TASK_ROW_WIDTHS.area,
          '--col-area-unit': TASK_ROW_WIDTHS.areaUnit,
          '--col-cost': TASK_ROW_WIDTHS.cost,
        } as React.CSSProperties
      }
    >
      <div
        className={MergeClassNames(
          'flex min-w-0 flex-1 items-center gap-2 pl-2 pr-2',
          isCompact ? 'py-0.5' : 'py-2'
        )}
      >
        <div className="-m-1 flex min-w-0 flex-1 flex-col justify-center rounded p-1 transition-colors hover:bg-gray-bright">
          <span className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
            {isCompact ? (
              <span className="min-w-0 truncate pl-6 text-sm text-gray-dark">
                <span className="font-bold">#{index + 1}</span>{' '}
                {getInputTaskTypeLabel(task.taskType)}
              </span>
            ) : (
              <button
                type="button"
                className="flex min-w-0 items-center gap-2 text-left"
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleExpand(task.taskId);
                }}
                aria-label={
                  isExpanded
                    ? TASKS_GRID_COLLAPSE_STEPS_ARIA_LABEL
                    : TASKS_GRID_EXPAND_STEPS_ARIA_LABEL
                }
              >
                <FaCircleChevronRight
                  size={16}
                  className={MergeClassNames(
                    'flex-shrink-0 text-secondary-dark transition-transform duration-150',
                    isExpanded ? 'rotate-90' : 'rotate-0'
                  )}
                />
                <span className="min-w-0 truncate">
                  <span className="font-bold">#{index + 1}</span>{' '}
                  {getInputTaskTypeLabel(task.taskType)}
                </span>
              </button>
            )}
            {rowAddInput && (
              // The row toggles expand on click; adding a product shouldn't.
              <span
                className="flex-shrink-0"
                onClick={(event) => event.stopPropagation()}
              >
                {rowAddInput}
              </span>
            )}
            {inlineDetails}
          </span>
          {!isCompact && !isExpanded && stepNamesLabel && (
            <span className="truncate text-xs font-medium text-gray-muted">
              {stepNamesLabel}
            </span>
          )}
        </div>
      </div>
      <div
        className="task-row-section flex w-[var(--col-date)] min-w-0 flex-shrink-0 items-stretch"
        {...{ [TASK_DATE_CELL_ATTR]: task.taskId }}
      >
        <EditableCell
          type="date"
          className="h-full w-full rounded"
          value={task.endDate}
          hasDay={task.endDateHasDay}
          displayValue={formatDate(task.endDate, task.endDateHasDay)}
          fallbackDate={selectedInputStartDate}
          onCommit={(endDate, endDateHasDay) =>
            commitTaskField(task, { endDate, endDateHasDay })
          }
          onEditEnd={() => onDateEditEnd?.(task.taskId)}
        />
      </div>
      {/* An other cost has no timing (OP-2399): the cell stays, empty, so
          the columns line up. Its area is the one its $/ha costs use. */}
      <div className="task-row-section flex w-[var(--col-timing)] min-w-0 flex-shrink-0 items-stretch">
        {!isOtherCost && (
          <TimingCell task={task} commitTaskField={commitTaskField} />
        )}
      </div>
      <div className="task-row-section flex w-[var(--col-area)] min-w-0 flex-shrink-0 items-stretch">
        <NumericInputField
          className="h-full w-full"
          inputClassName="h-full w-full rounded"
          min={0}
          value={task.area}
          displayValue={task.area ?? '-'}
          onCommit={(area) => commitTaskField(task, { area })}
        />
      </div>
      <div className="task-row-section flex w-[var(--col-area-unit)] min-w-0 flex-shrink-0 items-stretch">
        <EditableCell
          type="select"
          className="h-full w-full rounded"
          value={task.areaUnit?.globalId ?? null}
          displayValue={task.areaUnit?.unit || ' '}
          options={areaUnitOptions}
          placeholder={TASKS_GRID_SELECT_UNIT_PLACEHOLDER}
          allowClear
          onCommit={(globalId) => {
            if (globalId === null) {
              commitTaskField(task, { areaUnit: null });
              return;
            }
            const selected = gUnits.find((u) => u.globalId === globalId);
            if (!selected) return;
            commitTaskField(task, {
              areaUnit: { unit: selected.code, globalId: selected.globalId },
            });
          }}
        />
      </div>
      {/* The three-dot button sits inside this cell, not beside it: the pill
          draws a divider on every child except the last, so a sibling would
          put a separator on the cost cell that was never there. */}
      <div className="task-row-section flex w-[var(--col-cost)] flex-shrink-0 items-stretch justify-end gap-1 pl-2 pr-2">
        <div className="flex min-w-0 flex-1 flex-col items-end">
          <span className="flex w-full flex-1 items-center justify-end px-1.5 py-0.5 text-sm font-semibold">
            {formatCurrency(isFiltered ? subtotal : total)}
          </span>
          {isFiltered && (
            <span className="text-xs text-gray-muted">
              {formatCurrency(total)}
            </span>
          )}
        </div>
        <RowKebabButton
          label={TASKS_GRID_TASK_ACTIONS_ARIA_LABEL}
          isActive={isMenuOpen}
          onOpen={(event) =>
            onOpenMenuFromKebab(event, { rowType: 'task', taskId: task.taskId })
          }
        />
      </div>
    </div>
  );
};

export default TaskSummaryRow;
